import { AY38910 } from './ay38910';
import { Sequencer } from './sequencer';
import { PLAY_SONG } from './music';
import { effect, type EffectName } from './sound-effects';
import { shouldPlayMusic } from './music-events';
import { licensedManifest, loadLicensed } from './audio-assets';
import type { GameEvent, GameState } from '../game/state';

export interface AudioPreferences { music:boolean; musicVolume:number; sfxVolume:number; audioMode:'original-composition'|'licensed' }
export class AudioEngine {
  context:AudioContext|null=null;
  private psg:AY38910|null=null;
  private master:GainNode|null=null;
  private sequencer=new Sequencer();
  private pending:Promise<void>|null=null;
  private licensed:Map<string,AudioBuffer>|null=null;
  private sample:AudioBufferSourceNode|null=null;
  private sampleGain:GainNode|null=null;
  private musicState=false;
  private paused=false;
  error:string|null=null;
  onInterruption:()=>void=()=>{};
  preferences:AudioPreferences={music:true,musicVolume:0.4,sfxVolume:0.7,audioMode:'original-composition'};
  get unlocked():boolean{return !!this.psg;}
  get licensedAvailable():boolean{return this.licensed!==null;}
  get isMusicPlaying():boolean{return this.sequencer.playing||this.sample!==null;}
  async unlock():Promise<void>{
    if(this.pending)return this.pending;
    if(this.context && this.psg){this.paused=false;await this.context.resume();return;}
    // Called only from a trusted start/control/menu interaction, never at boot.
    const AudioCtx=window.AudioContext||(window as unknown as {webkitAudioContext:typeof AudioContext}).webkitAudioContext;
    if(!AudioCtx){this.error='Web Audio wird von diesem Browser nicht unterstützt.';return;}
    this.context=new AudioCtx({latencyHint:'interactive'});
    this.context.addEventListener('statechange',()=>{
      const state=this.context?.state as string;
      if(this.psg&&!this.paused&&(state==='interrupted'||state==='suspended'))this.onInterruption();
    });
    this.master=this.context.createGain();this.master.gain.value=0.85;
    const filter=this.context.createBiquadFilter();filter.type='lowpass';filter.frequency.value=6800;
    this.master.connect(filter);filter.connect(this.context.destination);
    const context=this.context;
    // resume immediately within the user gesture before awaiting module loading.
    const resumed=context.resume();
    this.pending=(async()=>{
      try {this.psg=await AY38910.create(context,this.master!);await resumed;this.licensed=await loadLicensed(context,licensedManifest);this.configure(this.preferences);this.error=null;}
      catch(e){this.error=`Audio konnte nicht gestartet werden: ${String(e)}`;await context.close();this.context=null;}
      finally{this.pending=null;}
    })();return this.pending;
  }
  configure(prefs:AudioPreferences):void {
    this.preferences={...prefs};this.psg?.gain(prefs.music?prefs.musicVolume:0,prefs.sfxVolume);
    if(this.sampleGain)this.sampleGain.gain.value=prefs.music?prefs.musicVolume:0;
    if(!prefs.music)this.stopMusic();
  }
  playEffect(name:EffectName,cause?:string):void {
    if(!this.context||!this.psg||this.paused)return;
    this.psg.schedule(effect(name,this.context.currentTime,cause));
  }
  event(event:GameEvent):void {
    if(['death','level','game-over','home'].includes(event.type))this.stopMusic();
    this.playEffect(event.type,event.cause);
    const key:Record<string,string>={start:'start',home:'home',death:'death',level:'level','game-over':'gameOver'};
    if(this.preferences.audioMode==='licensed'&&key[event.type])this.playSample(key[event.type]!,false);
  }
  update(state:GameState):void {
    if(!this.context||!this.psg||this.paused||this.context.state!=='running')return;
    const wants=shouldPlayMusic(state)&&this.preferences.music;
    if(wants&&!this.musicState){
      this.musicState=true;
      if(this.preferences.audioMode==='licensed'&&this.licensed)this.playSample('play',true);
      else this.sequencer.start(PLAY_SONG,this.context.currentTime);
    } else if(!wants&&this.musicState)this.stopMusic();
    this.sequencer.update(this.context.currentTime,this.psg);
  }
  private playSample(key:string,loop:boolean):void {
    const buffer=this.licensed?.get(key);if(!buffer||!this.context||!this.master)return;
    this.sample?.stop();const source=this.context.createBufferSource();const gain=this.context.createGain();
    source.buffer=buffer;source.loop=loop;gain.gain.value=this.preferences.music?this.preferences.musicVolume:0;
    source.connect(gain);gain.connect(this.master);this.sample=source;this.sampleGain=gain;
    source.onended=()=>{source.disconnect();gain.disconnect();if(this.sample===source){this.sample=null;this.sampleGain=null;}};source.start();
  }
  stopMusic():void {
    this.sequencer.stop();this.psg?.clear();this.musicState=false;
    this.sample?.stop();this.sample=null;this.sampleGain=null;
  }
  async suspend():Promise<void>{
    this.paused=true;if(this.context?.state==='running')await this.context.suspend();
  }
  async resume():Promise<void>{
    this.paused=false;
    if(['suspended','interrupted'].includes(this.context?.state as string))await this.context?.resume();
  }
}
